/**
 * PATH: packages/email-hub/src/feature/components/rich-text/suggestionController.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-SUGGESTCTRL-001
 * PURPOSE: Bridge ProseMirror's imperative Suggestion plugin (used by the
 *          @mention picker and the "/" slash menu) into a single React-
 *          rendered popup. The plugin owns keyboard navigation; the popup
 *          is purely presentational and reads its state from the controller.
 * WHY:     Two suggestion surfaces share one tested popup + one external
 *          store, so positioning, theming and a11y don't drift.
 */

import { useSyncExternalStore } from 'react'
import type { ComponentType } from 'react'

export interface SuggestItem {
  id: string
  title: string
  hint?: string
  icon?: ComponentType<{ className?: string }>
}

export interface SuggestState {
  kind: 'mention' | 'slash' | 'greeting'
  items: SuggestItem[]
  index: number
  rect: { top: number; left: number; bottom: number } | null
  loading: boolean
}

export interface SuggestionController {
  get(): SuggestState | null
  set(next: SuggestState | null): void
  subscribe(listener: () => void): () => void
  /** Bind the active suggestion's raw items + select callback (the
   *  ProseMirror Suggestion plugin's `command`). Cleared on exit. */
  setActive(active: { raw: unknown[]; command: (item: unknown) => void } | null): void
  /** Resolve the item at `index` to the plugin command (mouse + Enter). */
  select(index: number): void
  /** Move the highlight without selecting (mouse hover). */
  setIndex(index: number): void
}

/** Tiny external store so the popup re-renders when the plugin mutates state. */
export function createSuggestionController(): SuggestionController {
  let state: SuggestState | null = null
  let active: { raw: unknown[]; command: (item: unknown) => void } | null = null
  const listeners = new Set<() => void>()
  const emit = () => listeners.forEach((l) => l())
  return {
    get: () => state,
    set: (next) => { state = next; emit() },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    setActive: (next) => { active = next },
    select: (index) => {
      if (!active) return
      const item = active.raw[index]
      if (item !== undefined) active.command(item)
    },
    setIndex: (index) => {
      if (!state) return
      state = { ...state, index }
      emit()
    },
  }
}

/** React hook returning the live suggestion state for a controller. */
export function useSuggestionState(controller: SuggestionController): SuggestState | null {
  return useSyncExternalStore(controller.subscribe, controller.get, controller.get)
}

/**
 * Presentational popup. Anchored fixed to the caret rect. Selection +
 * navigation are handled by the Suggestion plugin's onKeyDown — clicking a
 * row calls `onPick(index)`, which the plugin maps back to its command.
 */
export function SuggestionPopup({
  state,
  onPick,
  onHover,
}: {
  state: SuggestState | null
  onPick: (index: number) => void
  onHover: (index: number) => void
}) {
  if (!state || !state.rect) return null
  const { items, index, rect, loading } = state
  if (!loading && items.length === 0) return null
  return (
    <ul
      role="listbox"
      aria-label={
        state.kind === 'mention' ? 'Mention a contact'
          : state.kind === 'greeting' ? 'Suggest a name'
            : 'Insert block'
      }
      className="fixed z-[80] min-w-[240px] max-w-[340px] max-h-72 overflow-y-auto rounded-lg border border-border bg-popover py-1 shadow-lg"
      style={{ top: rect.bottom + 4, left: rect.left }}
    >
      {loading && items.length === 0 && (
        <li className="px-3 py-1.5 text-xs text-muted-foreground" role="status">Searching…</li>
      )}
      {items.map((item, i) => {
        const active = i === index
        const Icon = item.icon
        return (
          <li
            key={item.id}
            role="option"
            aria-selected={active}
            onMouseEnter={() => onHover(i)}
            onMouseDown={(e) => { e.preventDefault(); onPick(i) }}
            className={
              'flex items-center gap-2.5 px-3 py-1.5 cursor-pointer text-sm '
              + (active ? 'bg-accent' : 'hover:bg-accent/60')
            }
          >
            {Icon && (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border bg-background text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-foreground">{item.title}</div>
              {item.hint && <div className="truncate text-xs text-muted-foreground">{item.hint}</div>}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
